import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import type {
  DocumentationConnectivityResult,
  DocumentationSettings,
} from '@/services/admin/documentationService'
import { useTranslation } from 'react-i18next'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface DocumentationContentSettingsTabProps {
  isLoading: boolean
  settingsLoadError?: string | null
  settings?: DocumentationSettings
  connectivityResult: DocumentationConnectivityResult | null
  settingsRepository: string
  settingsBranch: string
  settingsPathGlob: string
  settingsCacheSoftTtl: string
  settingsCacheTtl: string
  settingsWebhookDedupTtl: string
  minDocsCacheTtlSeconds: number
  settingsCacheSoftTtlInvalid: boolean
  settingsCacheTtlInvalid: boolean
  settingsWebhookDedupTtlInvalid: boolean
  settingsDirty: boolean
  settingsInputInvalid: boolean
  savePending: boolean
  connectivityPending: boolean
  settingsFetching: boolean
  onSetSettingsRepository: (value: string) => void
  onSetSettingsBranch: (value: string) => void
  onSetSettingsPathGlob: (value: string) => void
  onSetSettingsCacheSoftTtl: (value: string) => void
  onSetSettingsCacheTtl: (value: string) => void
  onSetSettingsWebhookDedupTtl: (value: string) => void
  onSave: () => void
  onReset: () => void
  onRunConnectivityTest: () => void
  onRefreshSettings: () => void
  formatDateTime: (value?: string | null) => string
}

export function DocumentationContentSettingsTab({
  isLoading,
  settingsLoadError,
  settings,
  connectivityResult,
  settingsRepository,
  settingsBranch,
  settingsPathGlob,
  settingsCacheSoftTtl,
  settingsCacheTtl,
  settingsWebhookDedupTtl,
  minDocsCacheTtlSeconds,
  settingsCacheSoftTtlInvalid,
  settingsCacheTtlInvalid,
  settingsWebhookDedupTtlInvalid,
  settingsDirty,
  settingsInputInvalid,
  savePending,
  connectivityPending,
  settingsFetching,
  onSetSettingsRepository,
  onSetSettingsBranch,
  onSetSettingsPathGlob,
  onSetSettingsCacheSoftTtl,
  onSetSettingsCacheTtl,
  onSetSettingsWebhookDedupTtl,
  onSave,
  onReset,
  onRunConnectivityTest,
  onRefreshSettings,
  formatDateTime,
}: DocumentationContentSettingsTabProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <HStack py={8} justify="center">
        <Spinner />
        <Text>{t('documentationContent.settings.loadingSettings')}</Text>
      </HStack>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      {settingsLoadError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{settingsLoadError}</Text>
        </Alert>
      ) : null}

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.settings.contentSource')}
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl>
            <FormLabel mb={1}>{t('documentationContent.settings.repository')}</FormLabel>
            <Input
              value={settingsRepository}
              onChange={(event) => onSetSettingsRepository(event.target.value)}
              placeholder={t('documentationContent.settings.repositoryPlaceholder')}
            />
          </FormControl>
          <FormControl>
            <FormLabel mb={1}>{t('documentationContent.settings.branch')}</FormLabel>
            <Input
              value={settingsBranch}
              onChange={(event) => onSetSettingsBranch(event.target.value)}
              placeholder={t('documentationContent.settings.branchPlaceholder')}
            />
          </FormControl>
          <FormControl gridColumn={{ base: 'auto', md: 'span 2' }}>
            <FormLabel mb={1}>{t('documentationContent.settings.pathGlob')}</FormLabel>
            <Input
              value={settingsPathGlob}
              onChange={(event) => onSetSettingsPathGlob(event.target.value)}
              placeholder={t('documentationContent.settings.pathGlobPlaceholder')}
            />
          </FormControl>
        </SimpleGrid>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.settings.runtimeCache')}
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Text>
            <Text as="span" color="gray.500">DOCS_CACHE:</Text>{' '}
            {settings?.cache.enabled
              ? t('documentationContent.settings.bound')
              : t('documentationContent.settings.notBound')}
          </Text>
          <FormControl isInvalid={settingsCacheTtlInvalid}>
            <FormLabel mb={1}>{t('documentationContent.settings.ttlSeconds')}</FormLabel>
            <Input
              type="number"
              min={minDocsCacheTtlSeconds}
              value={settingsCacheTtl}
              onChange={(event) => onSetSettingsCacheTtl(event.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('documentationContent.settings.minSeconds', {
                seconds: minDocsCacheTtlSeconds,
              })}
            </Text>
          </FormControl>
          <FormControl isInvalid={settingsCacheSoftTtlInvalid}>
            <FormLabel mb={1}>{t('documentationContent.settings.softTtlSeconds')}</FormLabel>
            <Input
              type="number"
              min={minDocsCacheTtlSeconds}
              value={settingsCacheSoftTtl}
              onChange={(event) => onSetSettingsCacheSoftTtl(event.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('documentationContent.settings.softTtlHint', {
                seconds: minDocsCacheTtlSeconds,
              })}
            </Text>
          </FormControl>
          <FormControl isInvalid={settingsWebhookDedupTtlInvalid}>
            <FormLabel mb={1}>{t('documentationContent.settings.webhookDedupTtl')}</FormLabel>
            <Input
              type="number"
              min={minDocsCacheTtlSeconds}
              value={settingsWebhookDedupTtl}
              onChange={(event) => onSetSettingsWebhookDedupTtl(event.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('documentationContent.settings.minSeconds', {
                seconds: minDocsCacheTtlSeconds,
              })}
            </Text>
          </FormControl>
        </SimpleGrid>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.settings.authSecretStatus')}
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <HStack>
            <Text color="gray.500">{t('documentationContent.settings.webhookSecret')}:</Text>
            <Badge colorScheme={settings?.auth.webhookSecretConfigured ? 'green' : 'red'}>
              {settings?.auth.webhookSecretConfigured
                ? t('documentationContent.settings.configured')
                : t('documentationContent.settings.missing')}
            </Badge>
          </HStack>
          <HStack>
            <Text color="gray.500">{t('documentationContent.settings.githubReadToken')}:</Text>
            <Badge colorScheme={settings?.auth.githubReadTokenConfigured ? 'green' : 'yellow'}>
              {settings?.auth.githubReadTokenConfigured
                ? t('documentationContent.settings.configured')
                : t('documentationContent.settings.notConfigured')}
            </Badge>
          </HStack>
        </SimpleGrid>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.settings.connectivityTest')}
        </Text>
        <HStack spacing={3} flexWrap="wrap">
          <Button
            colorScheme="teal"
            onClick={onSave}
            isLoading={savePending}
            isDisabled={!settingsDirty || settingsInputInvalid}
          >
            {t('documentationContent.settings.saveSettings')}
          </Button>
          <Button onClick={onReset} isDisabled={!settingsDirty || savePending}>
            {t('common.reset')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={onRunConnectivityTest}
            isLoading={connectivityPending}
            isDisabled={Boolean(settingsLoadError)}
          >
            {t('documentationContent.settings.runConnectivityTest')}
          </Button>
          <RefreshIconButton
            label={t('documentationContent.settings.refreshSettings')}
            onClick={onRefreshSettings}
            isLoading={settingsFetching}
          />
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={2}>
          {settingsDirty
            ? t('documentationContent.settings.unsavedChanges')
            : t('documentationContent.settings.settingsInSync')}
        </Text>
        {connectivityResult ? (
          <Box mt={3} borderWidth="1px" borderRadius="md" p={3}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              <Text>
                <Text as="span" color="gray.500">{t('common.status')}:</Text>{' '}
                {connectivityResult.ok
                  ? t('documentationContent.settings.ok')
                  : t('documentationContent.settings.failed')}
              </Text>
              <Text>
                <Text as="span" color="gray.500">
                  {t('documentationContent.settings.checkedAt')}:
                </Text>{' '}
                {formatDateTime(connectivityResult.checkedAt)}
              </Text>
              <Text>
                <Text as="span" color="gray.500">
                  {t('documentationContent.settings.githubStatus')}:
                </Text>{' '}
                {connectivityResult.githubStatus ?? t('common.na')}
              </Text>
              <Text>
                <Text as="span" color="gray.500">
                  {t('documentationContent.settings.defaultBranch')}:
                </Text>{' '}
                {connectivityResult.defaultBranch ?? t('common.na')}
              </Text>
              <Text>
                <Text as="span" color="gray.500">
                  {t('documentationContent.settings.privateRepo')}:
                </Text>{' '}
                {connectivityResult.privateRepository === null
                  ? t('common.na')
                  : connectivityResult.privateRepository
                    ? t('common.yes')
                    : t('common.no')}
              </Text>
              <Text>
                <Text as="span" color="gray.500">
                  {t('documentationContent.settings.message')}:
                </Text>{' '}
                {connectivityResult.message}
              </Text>
            </SimpleGrid>
          </Box>
        ) : null}
      </Box>
    </VStack>
  )
}
